import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import * as constants from 'src/app/common/constant/constants.type';
import { MedicalReportType, MedicalReportTypeLabels } from 'src/app/common/enums/medical-report-type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { MaterialModule } from 'src/app/material.module';
import { MedicalRecordDetailComponent } from './medical-record-detail/medical-record-detail.component';
import { MedicalRecordService } from './medical.service';
import { IMedicalRecord } from './medical.type';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { LazyPreviewDirective } from './lazy-preview.directive';

@Component({
  selector: 'app-medical-record',
  imports: [CommonModule, MaterialModule, TablerIconsModule, LazyPreviewDirective],
  providers: [DatePipe],
  templateUrl: './medical-record.component.html',
  styleUrl: './medical-record.component.scss',
})
export class MedicalRecordComponent implements OnInit, OnDestroy, OnChanges {
  @Input() patientId!: string | number | undefined;
  @Input() opdId?: number;
  @Input() type?: string;
  @Output() medicalRecordLength = new EventEmitter<number>();

  medicalRecords: IMedicalRecord[] = [];

  readonly medicalReportType = MedicalReportType;
  readonly medicalReportTypeLabels = MedicalReportTypeLabels;

  private destroy$ = new Subject<void>();
  private previewObjectUrls = new Set<string>();
  private recordsVersion = 0;
  private readonly previewQueue: Array<{ record: IMedicalRecord; version: number }> = [];
  private readonly queuedPreviewIds = new Set<number>();
  private readonly previewWindows = new Map<number, Window>();
  private activePreviewDownloads = 0;
  private readonly maxPreviewDownloads = 3;

  constructor(
    public dialog: MatDialog,
    private medicalRecordService: MedicalRecordService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private translationUtils: TranslationUtils,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Handled by ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['patientId'] || changes['opdId']) && this.patientId) {
      this.loadMedicalRecords();
    }
  }

  ngOnDestroy(): void {
    this.previewQueue.length = 0;
    this.queuedPreviewIds.clear();
    this.previewWindows.forEach(preview => preview.close());
    this.previewWindows.clear();
    this.releasePreviewObjectUrls();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDownload(record: IMedicalRecord): void {
    if (record.previewBlob) {
      this.triggerDownload(record.previewBlob, record.fileName || 'medical-record');
      return;
    }

    this.medicalRecordService.downloadMedicalRecord(record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ body, headers }) => {
          if (!body) return;
          this.triggerDownload(body, this.extractFileName(headers, record.fileName));
        },
        error: error => this.showDownloadError(error)
      });
  }

  onPreviewVisible(record: IMedicalRecord): void {
    if (!this.isPreviewable(record) || record.previewObjectUrl || this.queuedPreviewIds.has(record.id)) {
      return;
    }

    this.queuedPreviewIds.add(record.id);
    this.previewQueue.push({ record, version: this.recordsVersion });
    this.drainPreviewQueue();
  }

  private showDownloadError(error: unknown): void {
    this.resolveDownloadErrorMessage(error)
      .then(message => this.toastr.error(message));
  }

  private async resolveDownloadErrorMessage(error: unknown): Promise<string> {
    const fallback = 'Unable to download medical record.';
    const responseError = error instanceof HttpErrorResponse ? error.error : error;

    if (responseError instanceof Blob) {
      try {
        const errorText = await responseError.text();
        const parsedError = errorText ? JSON.parse(errorText) : null;
        return this.getApiErrorMessage(parsedError, fallback);
      } catch {
        return fallback;
      }
    }

    return this.getApiErrorMessage(responseError, fallback);
  }

  private getApiErrorMessage(error: any, fallback: string): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error?.message) {
      return error.message;
    }
    if (Array.isArray(error?.errors)) {
      return error.errors.join('\n');
    }
    if (error?.errors && typeof error.errors === 'object') {
      const errors = Object.values(error.errors).flat().filter(Boolean);
      if (errors.length) {
        return errors.join('\n');
      }
    }

    return fallback;
  }

  private extractFileName(headers: HttpHeaders, fallback = 'medical-record'): string {
    const contentDisposition = headers.get('content-disposition') || '';
    const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const normal = contentDisposition.match(/filename="?([^"]+)"?/i)?.[1];

    return decodeURIComponent(utf8 || normal || fallback);
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  }

  openPreview(record: IMedicalRecord): void {
    if (record.previewObjectUrl) {
      window.open(record.previewObjectUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!this.isPreviewable(record)) {
      this.onDownload(record);
      return;
    }

    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      this.previewWindows.set(record.id, previewWindow);
    }
    this.onPreviewVisible(record);
  }

  onPreviewLoadError(record: IMedicalRecord): void {
    this.releaseRecordPreviewUrl(record);
    record.previewType = 'file';
    record.previewUrl = '';
    this.closePreviewWindow(record.id);
  }

  onDelete(record: IMedicalRecord): void {
    this.medicalRecordService.deleteMedicalRecord(record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res.isSuccess) return;

        this.toastr.success(
          this.translationUtils.getTranslatedMessage(
            'COMMON.DELETE',
            this.translate.instant('ENTITY.RECORD')
          )
        );
        this.loadMedicalRecords();
      });
  }

  onUpload(): void {
    this.dialog.open(MedicalRecordDetailComponent, {
      width: '100vw',
      maxWidth: '600px',
      disableClose: true,
      data: { patientId: this.patientId }
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => res && this.loadMedicalRecords());
  }

 loadMedicalRecords(): void {
  this.medicalRecordService.getMedicalRecordByPatientID(Number(this.patientId), this.opdId)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res) => {
        if (!this.isValidResponse(res)) {
          this.resetRecords();
          return;
        }

        const filteredRecords = this.type 
          ? this.filterRecordsByType(res.result, this.type)
          : res.result;

        this.updateMedicalRecords(filteredRecords);
      },
      error: (error) => {
        console.error('Failed to load medical records:', error);
        this.resetRecords();
      }
    });
}

private isValidResponse(res: any): boolean {
  return res?.isSuccess && Array.isArray(res.result) && res.result.length > 0;
}

private filterRecordsByType(records: any[], type: string): any[] {
  const normalizedType = type.toLowerCase().trim();
  const reportType = this.getReportTypeEnum(normalizedType);

  return records.filter(record => 
    this.matchesRecordType(record, normalizedType, reportType)
  );
}

private getReportTypeEnum(typeStr: string): MedicalReportType | null {
  const typeMap: Record<string, MedicalReportType> = {
    'ipd': MedicalReportType.IPD,
    'opd': MedicalReportType.OPD,
    'pastdocument': MedicalReportType.PastDocument
  };

  return typeMap[typeStr] ?? null;
}

private matchesRecordType(record: any, typeStr: string, reportType: MedicalReportType | null): boolean {
  if (reportType === null) return false;

  // Normalize record type for comparison
  const recordType = record.type;
  
  return (String(recordType).toLowerCase() === typeStr || recordType === reportType ||Number(recordType) === reportType);
}

private updateMedicalRecords(records: any[]): void {
  this.releasePreviewObjectUrls();
  this.recordsVersion++;
  this.previewQueue.length = 0;
  this.queuedPreviewIds.clear();
  this.medicalRecords = records.map(this.mapRecord.bind(this));
  this.medicalRecordLength.emit(this.medicalRecords.length);
}

  private resetRecords(): void {
    this.releasePreviewObjectUrls();
    this.recordsVersion++;
    this.medicalRecords = [];
    this.medicalRecordLength.emit(0);
  }

  private mapRecord(record: IMedicalRecord): IMedicalRecord {
    const fileName = record.originalFileName || 'medical-record';
    const previewType = this.getPreviewType(record.contentType, fileName);

    return {
      ...record,
      description: record.description || 'Medical Record',
      uploadDate: this.datePipe.transform(record.uploadDate, constants.DDMMYYYY_FORMAT) as string,
      fileName,
      previewType,
      previewUrl: ''
    };
  }

  private drainPreviewQueue(): void {
    while (this.activePreviewDownloads < this.maxPreviewDownloads && this.previewQueue.length > 0) {
      const preview = this.previewQueue.shift()!;
      this.loadPreview(preview.record, preview.version);
    }
  }

  private loadPreview(record: IMedicalRecord, version: number): void {
    this.activePreviewDownloads++;
    this.medicalRecordService.downloadMedicalRecord(record.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.completePreview(record.id))
      )
      .subscribe({
        next: ({ body }) => this.applyPreview(record, version, body),
        error: () => this.markPreviewUnavailable(record, version)
      });
  }

  private completePreview(recordId: number): void {
    this.activePreviewDownloads--;
    this.queuedPreviewIds.delete(recordId);
    this.drainPreviewQueue();
  }

  private markPreviewUnavailable(record: IMedicalRecord, version: number): void {
    if (version !== this.recordsVersion) {
      return;
    }

    record.previewType = 'file';
    record.previewUrl = '';
    this.closePreviewWindow(record.id);
  }

  private isPreviewable(record: IMedicalRecord): boolean {
    return record.previewType === 'image' || record.previewType === 'pdf';
  }

  private applyPreview(record: IMedicalRecord, version: number, body: Blob | null): void {
    if (!body) {
      return;
    }

    const objectUrl = URL.createObjectURL(body);
    if (version !== this.recordsVersion) {
      URL.revokeObjectURL(objectUrl);
      this.closePreviewWindow(record.id);
      return;
    }

    this.previewObjectUrls.add(objectUrl);
    record.previewBlob = body;
    record.previewObjectUrl = objectUrl;
    record.previewUrl = record.previewType === 'pdf'
      ? this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl)
      : objectUrl;

    const previewWindow = this.previewWindows.get(record.id);
    if (previewWindow) {
      previewWindow.location.href = objectUrl;
      this.previewWindows.delete(record.id);
    }
  }

  private closePreviewWindow(recordId: number): void {
    this.previewWindows.get(recordId)?.close();
    this.previewWindows.delete(recordId);
  }

  private releaseRecordPreviewUrl(record: IMedicalRecord): void {
    if (!record.previewObjectUrl) return;

    URL.revokeObjectURL(record.previewObjectUrl);
    this.previewObjectUrls.delete(record.previewObjectUrl);
    record.previewObjectUrl = undefined;
    record.previewBlob = undefined;
  }

  private releasePreviewObjectUrls(): void {
    this.previewObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.previewObjectUrls.clear();
  }

  getRecordTypeLabel(type: MedicalReportType): string {
    return this.medicalReportTypeLabels[type] ?? String(type);
  }

  getFileBadgeLabel(record: IMedicalRecord): string {
    return this.getFileExtension(record.fileName || '')?.toUpperCase() || 'FILE';
  }

  private getFileExtension(fileName: string): string {
    return fileName.split('.').pop()?.toLowerCase() || '';
  }

  // Content type comes from the API for new documents; legacy rows only have
  // a file name, so fall back to its extension.
  private getPreviewType(contentType: string | undefined, fileName: string): IMedicalRecord['previewType'] {
    if (contentType?.startsWith('image/')) {
      return 'image';
    }

    if (contentType === 'application/pdf') {
      return 'pdf';
    }

    return this.getPreviewTypeFromExtension(fileName);
  }

  private getPreviewTypeFromExtension(fileName: string): IMedicalRecord['previewType'] {
    const ext = this.getFileExtension(fileName);

    if (['png', 'jpg', 'jpeg', 'jfif', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
      return 'image';
    }

    if (ext === 'pdf') {
      return 'pdf';
    }

    return 'file';
  }

}
